import { FormEvent, ReactNode, useEffect, useState } from "react";
import { Edit, Plus, RefreshCw, Save, Search, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminOperationLocation,
  AdminOperationLocationPayload,
  AdminUser,
  OperationLocationDisplayPage,
  PaginationMeta,
  deleteAdminOperationLocation,
  getCurrentAdmin,
  listAdminOperationLocations,
  logout,
  saveAdminOperationLocation,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminSwitchCard } from "./components/AdminChoiceControl";
import Pagination from "./components/Pagination";
import { getDistrictsByCity, getPostalCode, taiwanCities } from "@/data/taiwanAddress";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

type FormState = {
  CountryCode: string;
  CountryName: string;
  Name: string;
  Tag: string;
  IsHeadquarters: boolean;
  AddressCity: string;
  AddressDistrict: string;
  AddressLine: string;
  Phone: string;
  BusinessHoursText: string;
  DisplayOn: OperationLocationDisplayPage[];
  IsActive: boolean;
  SortOrder: string;
};

const displayPageOptions: { value: OperationLocationDisplayPage; label: string; description: string }[] = [
  { value: "About", label: "關於我們", description: "顯示於前台 /about 頁面的「台灣營運據點」區塊" },
  { value: "Contact", label: "聯絡我們", description: "顯示於前台 /contact 頁面的「門市資訊」區塊" },
];

const emptyForm: FormState = {
  CountryCode: "TW",
  CountryName: "台灣",
  Name: "",
  Tag: "分店",
  IsHeadquarters: false,
  AddressCity: "",
  AddressDistrict: "",
  AddressLine: "",
  Phone: "",
  BusinessHoursText: "",
  DisplayOn: ["About", "Contact"],
  IsActive: true,
  SortOrder: "100",
};

function itemToForm(item: AdminOperationLocation): FormState {
  return {
    CountryCode: item.CountryCode,
    CountryName: item.CountryName,
    Name: item.Name,
    Tag: item.Tag,
    IsHeadquarters: item.IsHeadquarters,
    AddressCity: item.AddressCity || "",
    AddressDistrict: item.AddressDistrict || "",
    AddressLine: item.AddressLine,
    Phone: item.Phone,
    BusinessHoursText: (item.BusinessHours || []).join("\n"),
    DisplayOn: item.DisplayOn && item.DisplayOn.length > 0 ? item.DisplayOn : ["About", "Contact"],
    IsActive: item.IsActive,
    SortOrder: String(item.SortOrder || 100),
  };
}

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

function FormSectionLabel({ children }: { children: ReactNode }) {
  return <h3 className="text-xs font-bold uppercase tracking-wide text-[#9aa59f]">{children}</h3>;
}

export default function AdminOperationLocations() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminOperationLocation[]>([]);
  const [counts, setCounts] = useState({ Active: 0, Inactive: 0 });
  const [countries, setCountries] = useState<{ CountryCode: string; CountryName: string }[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [editingItem, setEditingItem] = useState<AdminOperationLocation | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminOperationLocations(filters);
      setItems(data.Items);
      setCounts(data.Counts || { Active: 0, Inactive: 0 });
      setCountries(data.Countries || []);
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "營運據點資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  function openCreateForm() {
    setEditingItem(null);
    setForm(emptyForm);
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditForm(item: AdminOperationLocation) {
    setEditingItem(item);
    setForm(itemToForm(item));
    setFormMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  }

  const formDistricts = getDistrictsByCity(form.AddressCity);
  const formPostalCode = getPostalCode(form.AddressCity, form.AddressDistrict);

  function handleCityChange(city: string) {
    setForm((current) => ({ ...current, AddressCity: city, AddressDistrict: "" }));
  }

  function toggleDisplayOn(page: OperationLocationDisplayPage) {
    setForm((current) => ({
      ...current,
      DisplayOn: current.DisplayOn.includes(page)
        ? current.DisplayOn.filter((item) => item !== page)
        : [...current.DisplayOn, page],
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.Name.trim()) { setFormMessage("請輸入據點名稱。"); return; }
    if (!form.CountryCode.trim()) { setFormMessage("請輸入國家代碼。"); return; }
    if (!form.CountryName.trim()) { setFormMessage("請輸入國家名稱。"); return; }
    if (!form.AddressCity.trim()) { setFormMessage("請選擇縣市。"); return; }
    if (!form.AddressDistrict.trim()) { setFormMessage("請選擇鄉鎮市區。"); return; }
    if (!form.AddressLine.trim()) { setFormMessage("請輸入詳細地址。"); return; }

    const payload: AdminOperationLocationPayload = {
      CountryCode: form.CountryCode.trim().toUpperCase(),
      CountryName: form.CountryName.trim(),
      Name: form.Name.trim(),
      Tag: form.Tag.trim() || "分店",
      IsHeadquarters: form.IsHeadquarters,
      AddressCity: form.AddressCity,
      AddressDistrict: form.AddressDistrict,
      AddressPostalCode: formPostalCode || undefined,
      AddressLine: form.AddressLine.trim(),
      Phone: form.Phone.trim(),
      BusinessHours: form.BusinessHoursText.split("\n").map((line) => line.trim()).filter(Boolean),
      DisplayOn: form.DisplayOn,
      IsActive: form.IsActive,
      SortOrder: Number(form.SortOrder || 100),
    };

    setSaving(true);
    setFormMessage("");
    try {
      await saveAdminOperationLocation(editingItem?.LocationId || null, payload);
      closeForm();
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "營運據點儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: AdminOperationLocation) {
    const confirmed = await confirm({
      title: "刪除營運據點？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Name}</strong> 嗎？
          <br />
          刪除後前台「關於我們」頁面也會立即移除，且無法復原。
        </>
      ),
      confirmText: "刪除據點",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminOperationLocation(item.LocationId);
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "營運據點刪除失敗。");
    }
  }

  const allItemCount = counts.Active + counts.Inactive || pagination.Total;

  return (
    <AdminLayout sectionLabel="內容管理" title="營運據點管理" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">全部據點</p>
          <p className="mt-1 text-2xl font-bold text-[#203d32]">{allItemCount}</p>
        </button>
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Active", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">上架中</p>
          <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Active}</p>
        </button>
        <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Inactive", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
          <p className="text-xs font-bold text-[#7b877f]">已下架</p>
          <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Inactive}</p>
        </button>
      </div>

      {countries.length > 1 && (
        <p className="mb-3 text-xs text-[#7b877f]">目前資料涵蓋國家：{countries.map((country) => country.CountryName).join("、")}</p>
      )}

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="location-status">狀態</label>
          <select id="location-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Active">上架</option>
            <option value="Inactive">下架</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="location-keyword">關鍵字</label>
          <input id="location-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="據點名稱、地址或電話" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateForm} type="button">
          <Plus size={20} />
          新增據點
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[1000px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[100px] px-4 py-3">國家</th>
                <th className="w-[360px] px-4 py-3">據點</th>
                <th className="w-[130px] px-4 py-3">電話</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[80px] px-4 py-3">排序</th>
                <th className="w-[160px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>沒有符合條件的營運據點。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.LocationId}>
                  <td className="px-4 py-4 text-sm font-bold text-[#315746]">{item.CountryName}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <strong className="text-[#203d32]">{item.Name}</strong>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${item.IsHeadquarters ? "bg-[#f3ecdf] text-[#8a6a2f]" : "bg-[#eef3f8] text-[#255071]"}`}>{item.Tag}</span>
                    </div>
                    <div className="mt-1 text-xs text-[#7b877f]">
                      {item.AddressPostalCode ? `${item.AddressPostalCode} ` : ""}
                      {item.AddressCity}{item.AddressDistrict}{item.AddressLine}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-[#315746]">{item.Phone || "-"}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsActive ? "上架" : "下架"}</span>
                  </td>
                  <td className="px-4 py-4">{item.SortOrder}</td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openEditForm(item)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                        <Trash2 size={16} />
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative flex max-h-[90vh] w-full max-w-[680px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">{editingItem ? "編輯營運據點" : "新增營運據點"}</h2>
                <p>可分別設定要顯示於前台「關於我們」「聯絡我們」哪些頁面，依國家分組顯示。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-5 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

              <div className="grid gap-3">
                <FormSectionLabel>基本資料</FormSectionLabel>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>據點名稱 <span className="text-[#c3423f]">*</span></span>
                    <input className={inputClass} placeholder="台北門市" value={form.Name} onChange={(event) => setForm((current) => ({ ...current, Name: event.target.value }))} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>標籤</span>
                    <input className={inputClass} placeholder="總店 / 分店" value={form.Tag} onChange={(event) => setForm((current) => ({ ...current, Tag: event.target.value }))} />
                  </label>
                </div>
              </div>

              <div className="grid gap-3 border-t border-[#edf0ee] pt-4">
                <FormSectionLabel>所屬國家</FormSectionLabel>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>國家代碼 <span className="text-[#c3423f]">*</span></span>
                    <input className={`${inputClass} uppercase`} maxLength={2} placeholder="TW" value={form.CountryCode} onChange={(event) => setForm((current) => ({ ...current, CountryCode: event.target.value }))} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>國家名稱 <span className="text-[#c3423f]">*</span></span>
                    <input className={inputClass} placeholder="台灣" value={form.CountryName} onChange={(event) => setForm((current) => ({ ...current, CountryName: event.target.value }))} />
                  </label>
                </div>
                <p className="text-xs font-normal text-[#7b877f]">目前僅台灣（TW），未來擴充其他國家時可直接新增不同代碼，前後台都不需要再修改。</p>
              </div>

              <div className="grid gap-3 border-t border-[#edf0ee] pt-4">
                <FormSectionLabel>地址</FormSectionLabel>
                <div className="grid gap-4 sm:grid-cols-3">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>縣市 <span className="text-[#c3423f]">*</span></span>
                    <select className={`${inputClass} bg-white`} value={form.AddressCity} onChange={(event) => handleCityChange(event.target.value)}>
                      <option value="">請選擇縣市</option>
                      {taiwanCities.map((city) => <option key={city} value={city}>{city}</option>)}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>鄉鎮市區 <span className="text-[#c3423f]">*</span></span>
                    <select
                      className={`${inputClass} bg-white`}
                      disabled={!form.AddressCity}
                      value={form.AddressDistrict}
                      onChange={(event) => setForm((current) => ({ ...current, AddressDistrict: event.target.value }))}
                    >
                      <option value="">{form.AddressCity ? "請選擇鄉鎮市區" : "請先選擇縣市"}</option>
                      {formDistricts.map((district) => <option key={district.name} value={district.name}>{district.name}</option>)}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>郵遞區號</span>
                    <input className={`${inputClass} bg-[#f6f7f5] text-[#7b877f]`} placeholder="選擇鄉鎮市區後自動帶出" readOnly value={formPostalCode} />
                  </label>
                </div>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>詳細地址 <span className="text-[#c3423f]">*</span></span>
                  <input className={inputClass} placeholder="忠孝東路四段15號5樓之1" value={form.AddressLine} onChange={(event) => setForm((current) => ({ ...current, AddressLine: event.target.value }))} />
                </label>
              </div>

              <div className="grid gap-3 border-t border-[#edf0ee] pt-4">
                <FormSectionLabel>聯絡與排序</FormSectionLabel>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>電話</span>
                    <input className={inputClass} value={form.Phone} onChange={(event) => setForm((current) => ({ ...current, Phone: event.target.value }))} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>排序（數字越小越前面）</span>
                    <input className={inputClass} type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
                  </label>
                </div>
              </div>

              <div className="grid gap-3 border-t border-[#edf0ee] pt-4">
                <FormSectionLabel>營業時間</FormSectionLabel>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>每行一則，例如「週一至週五 09:00–18:00」</span>
                  <textarea className="min-h-[110px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.BusinessHoursText} onChange={(event) => setForm((current) => ({ ...current, BusinessHoursText: event.target.value }))} />
                </label>
              </div>

              <div className="grid gap-3 border-t border-[#edf0ee] pt-4">
                <FormSectionLabel>顯示於前台頁面</FormSectionLabel>
                <p className="text-xs font-normal text-[#7b877f]">勾選這個據點要出現在前台哪些頁面，未來新增頁面時這裡會直接多一個選項。</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {displayPageOptions.map((option) => (
                    <label key={option.value} className="flex cursor-pointer items-start gap-3 rounded-md border border-[#dfe6e1] p-3 hover:border-[#b8c8bf]">
                      <input
                        checked={form.DisplayOn.includes(option.value)}
                        className="mt-0.5 h-4 w-4 accent-[#315746]"
                        type="checkbox"
                        onChange={() => toggleDisplayOn(option.value)}
                      />
                      <span>
                        <span className="block text-sm font-bold text-[#315746]">{option.label}</span>
                        <span className="block text-xs font-normal text-[#7b877f]">{option.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {form.DisplayOn.length === 0 && (
                  <p className="text-xs font-medium text-[#9a6b2f]">目前沒有勾選任何頁面，這個據點暫時不會顯示在前台任何地方。</p>
                )}
              </div>

              <div className="grid gap-3 border-t border-[#edf0ee] pt-4">
                <FormSectionLabel>顯示設定</FormSectionLabel>
                <AdminSwitchCard checked={form.IsHeadquarters} description="標示為總店，前台會用特別樣式突顯。" label="設為總店" onChange={(IsHeadquarters) => setForm((current) => ({ ...current, IsHeadquarters }))} />
                <AdminSwitchCard checked={form.IsActive} description="下架後不會顯示於前台任何頁面。" label="上架顯示" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
              </div>
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存據點"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
